import { toSafeString } from '../utils/toSafeString.js';

/*
 * The one reader of a Pieve workflow step's connection pin, shared because both sides of the API
 * boundary read the same field: the run and step-run paths resolve which credential to bind, and the
 * workflow rail and step popover render what it binds. A second copy would be a second answer, and
 * the wrong answer here is not a blank field but a fabricated one.
 *
 * `step.connectionRef` is canonical at the step top level — where STEP_CONTRACTS declares it, where
 * resolveStepRefs writes it, and where the validator and credential collector read it. Sourcing it
 * from `step.options` instead silently dropped user-chosen pins and let the MCP resolver auto-rank a
 * different connection at run time.
 *
 * It holds EITHER a verbatim pin (a credential id or a connection label) OR a map from
 * `main.company_environment` id to such a pin, which is the whole of the multi-environment feature
 * (2026-09-02). That is exactly why nothing may read the raw field: `String({})` is
 * `"[object Object]"`, a truthy non-credential, so a raw read claims a pin on a credential id that
 * does not exist. Nine call sites did that before this became the single reader.
 *
 * The legacy `credentialId` alias is read here for parity with every other consumer. Authoring
 * coerces it to `connectionRef` and deletes it, so it only matters for steps persisted before that
 * coercion existed; execution must not resolve a different connection than the collector and
 * validator already agreed on.
 */
const getPieveWorkflowStepConnectionRef = ({ step, environmentId } = {}) => {
  const ref = step?.connectionRef;

  if (isPieveWorkflowStepConnectionRefMap(ref)) {
    return toSafeString(ref[toSafeString(environmentId)]);
  }

  // Any other object — `{}`, an array — maps nothing and so names no connection in any environment.
  // It must never reach `toSafeString`.
  if (ref && typeof ref === 'object') {
    return toSafeString(step?.credentialId);
  }

  return toSafeString(ref) || toSafeString(step?.credentialId);
};

// A map, as opposed to a pin. An empty object is not a map: a step whose object has no keys names no
// connection in any environment, which is the same fact as carrying no pin at all — so it must not
// block auto-pinning or satisfy TRIGGER_CONNECTION_REQUIRED.
export const isPieveWorkflowStepConnectionRefMap = (value) => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0;
};

// Every credential the step can bind, across all its environments. The credential collector needs
// this rather than one environment's answer: under-reporting is what would let a credential still
// used by Sandbox look unused, and the warning that deleting it breaks a live workflow never fire.
export const listPieveWorkflowStepConnectionRefs = ({ step } = {}) => {
  const ref = step?.connectionRef;

  if (isPieveWorkflowStepConnectionRefMap(ref)) {
    return [
      ...new Set(
        Object.values(ref)
          .map((value) => toSafeString(value))
          .filter(Boolean)
      )
    ];
  }

  const single = getPieveWorkflowStepConnectionRef({ step });

  return single ? [single] : [];
};

export default getPieveWorkflowStepConnectionRef;
